// 환경 조립: 배치(layout) → 키트 인스턴싱(종류당 InstancedMesh 1개 + CPU 시야 거르기), 타일 바닥, 고원 풀밭·낭떠러지·원경, 효과.
// 모든 로드는 start() 하나로 끝나고, 그 전에는 map.ts의 그레이박스가 보인다. 프레임마다 하는 일은 cull()뿐이며
// 카메라 목표가 움직였을 때만 인스턴스를 다시 모은다(할당 없음). 컷어웨이는 셰이더 uniform, 효과는 실시간 time.
import * as THREE from 'three/webgpu';
import type { QualityLevel } from '../../shared/config';
import type { MapDef, MapGrid, MapId } from '../../shared/levels';
import { compose } from '../util';
import { cardField, contactField, driftField, flameField, glowField, ivyField, poolField, shaftField, type PoolTheme } from './fx';
import { loadKit, loadTexture, track, type KitAsset } from './kits';
import { REGION_DRIFT, REGION_TEXTURES, kitRegionOf, legacyFromSearch, type KitRegion } from './regionKits';
import { layoutMap, type EnvLayout, type KitKind, type Placement, type PlateauField } from './layout';
import { gradeOf, moodOf } from './mood';
import { VillageNpcs } from './npcs';
import { REGION_LOOK, backdropMaterial, cliffMaterial, floorMaterial, groundMaterial, inlayMaterial, kitMaterial } from './surfaces';

export interface EnvResult {
  /** 키트로 바뀐 랜드마크(그레이박스 숨김) */
  replaced: Set<number>;
  /** 랜드마크 인덱스 → 모델 윗면 높이(이름표 위치) */
  tops: Map<number, number>;
  /** 벽 키트를 읽었으면 그레이박스 벽을 숨긴다 */
  walls: boolean;
  /** 타일 바닥이 생겼으면 체커 바닥을 숨긴다 */
  floor: boolean;
}

/**
 * 키트는 종류당 InstancedMesh 하나다. three는 InstancedMesh마다 object.uuid를 노드 캐시 키에 넣어
 * 메시가 늘면 노드 빌드(미리 컴파일 시간)가 그만큼 늘기 때문이다(구간별로 쪼갰을 때 R1 컴파일 16초).
 * 화면 밖 인스턴스는 cull()이 CPU에서 빼서 삼각형 수를 지킨다.
 */
interface KitBatch {
  mesh: THREE.InstancedMesh;
  cut: THREE.InstancedBufferAttribute;
  n: number;
  /** 원본 인스턴스 데이터(시야 거르기 때 앞쪽으로 모아 복사) */
  mat: Float32Array;
  cutSrc: Float32Array;
  col: Float32Array;
  /** x, z, 반경 */
  pos: Float32Array;
}
/** 시야 사각형 바깥 여유(m): 큰 건물 반경 + 늦은 오후 긴 그림자 */
const CULL_MARGIN = 14;
const _ndc = new THREE.Vector3();
const _dir = new THREE.Vector3();
const DENSITY: Record<QualityLevel, number> = { low: 0.5, mid: 0.75, high: 1 };
/** 그림자를 드리우는 종류(작은 소품은 그림자 그리기를 아낀다) */
const CASTERS: ReadonlySet<KitKind> = new Set<KitKind>([
  'wall', 'wall_broken', 'pillar', 'pillar_broken', 'arch', 'statue', 'tree', 'tree_lo', 'banner',
  'v_house', 'v_shop', 'v_forge', 'v_trainer', 'v_gate', 'v_fountain', 'v_questboard', 'v_lamppost',
]);
/**
 * 접지 그림자 세기(종류별). 그림자 맵은 해 방향으로 길게 빠져 소품 바로 밑은 비어 보였다(dream-loop 마을 4회차: 소품이 떠 보임).
 * 벽처럼 긴 것은 옅게(복도 가장자리가 통째로 검어지지 않게), 계단은 넣지 않는다.
 */
const CONTACT: Partial<Record<KitKind, number>> = {
  wall: 0.32, wall_broken: 0.32, pillar: 0.55, pillar_broken: 0.5, arch: 0.4, statue: 0.6, banner: 0.45, brazier: 0.55,
  rubble: 0.4, rock: 0.5, rock_lo: 0.45, tree: 0.6, tree_lo: 0.5, crates: 0.6, mushrooms: 0.35,
  v_house: 0.45, v_shop: 0.5, v_forge: 0.5, v_trainer: 0.5, v_gate: 0.45, v_fountain: 0.55, v_questboard: 0.55, v_lamppost: 0.5,
  village_well: 0.55, village_market_stall: 0.55, village_flower_planter: 0.55, village_banner_pole: 0.45, village_house_b: 0.45,
};

/** 배치 하나의 축별 배율(키트 bbox 기준) */
function scaleOf(p: Placement, a: KitAsset): [number, number, number] {
  if (p.h > 0 && p.w === 0 && p.d === 0) {
    const s = p.h / a.h;
    return [s, s, s];
  }
  if (p.h === 0) {
    const s = Math.min(p.w / a.w, p.d / a.d);
    return [s, s, s];
  }
  return [p.w / a.w, p.h / a.h, p.d / a.d];
}

/** 고원 윗면(풀밭)과 낭떠러지 옆면: 거리장 f의 0 등고선을 마칭 스퀘어로 따라 부드러운 가장자리를 만든다 */
function buildPlateau(pf: PlateauField, skipCell: (i: number, j: number) => boolean): { top: THREE.BufferGeometry; side: THREE.BufferGeometry } {
  const { nx, nz, f, originX: ox, originZ: oz } = pf;
  const tp: number[] = [], tc: number[] = [], sp: number[] = [], sn: number[] = [];
  const Y = -0.025, DROP = -11;
  const edgeCol = [0.55, 0.5, 0.36];
  const colOf = (v: number): [number, number, number] => {
    const t = Math.min(1, Math.max(0, v / 2.2));
    const k = t * t * (3 - 2 * t);
    return [edgeCol[0] + (1 - edgeCol[0]) * k, edgeCol[1] + (1 - edgeCol[1]) * k, edgeCol[2] + (1 - edgeCol[2]) * k];
  };
  const fAt = (x: number, z: number): number => {
    const fx = Math.min(nx - 1.001, Math.max(0, x - ox)), fz = Math.min(nz - 1.001, Math.max(0, z - oz));
    const i = Math.floor(fx), j = Math.floor(fz), u = fx - i, v = fz - j;
    const a = f[j * nx + i], b = f[j * nx + i + 1], c = f[(j + 1) * nx + i], d = f[(j + 1) * nx + i + 1];
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
  const poly: number[] = []; // x, z, f 세 개씩
  const cross: number[] = []; // 교차점 x, z
  for (let j = 0; j < nz - 1; j++) {
    for (let i = 0; i < nx - 1; i++) {
      const cs = [[i, j], [i + 1, j], [i + 1, j + 1], [i, j + 1]];
      const vs = cs.map(([a, b]) => f[b * nx + a]);
      const inside = vs.map((v) => v > 0);
      if (!inside.some(Boolean)) continue;
      if (inside.every(Boolean) && skipCell(i, j)) continue;
      poly.length = 0;
      cross.length = 0;
      for (let k = 0; k < 4; k++) {
        const k2 = (k + 1) & 3;
        if (inside[k]) poly.push(ox + cs[k][0], oz + cs[k][1], vs[k]);
        if (inside[k] !== inside[k2]) {
          const t = vs[k] / (vs[k] - vs[k2]);
          const x = ox + cs[k][0] + (cs[k2][0] - cs[k][0]) * t, z = oz + cs[k][1] + (cs[k2][1] - cs[k][1]) * t;
          poly.push(x, z, 0);
          cross.push(x, z);
        }
      }
      const n = poly.length / 3;
      for (let k = 1; k < n - 1; k++) {
        // 위를 향하도록 (0, k+1, k) 순서
        for (const q of [0, k + 1, k]) {
          tp.push(poly[q * 3], Y, poly[q * 3 + 1]);
          tc.push(...colOf(poly[q * 3 + 2]));
        }
      }
      for (let s = 0; s + 3 < cross.length; s += 4) {
        const ax = cross[s], az = cross[s + 1], bx = cross[s + 2], bz = cross[s + 3];
        let nxv = bz - az, nzv = -(bx - ax);
        const len = Math.hypot(nxv, nzv) || 1;
        nxv /= len; nzv /= len;
        const mx = (ax + bx) / 2, mz = (az + bz) / 2;
        if (fAt(mx + nxv * 0.3, mz + nzv * 0.3) > 0) { nxv = -nxv; nzv = -nzv; }
        // 삼각형 (a 위, b 위, b 아래)의 법선 = (-ez·h, 0, ex·h). 바깥(nx, nz)을 향하지 않으면 a·b를 바꾼다
        const ex = bx - ax, ez = bz - az, h = DROP - Y;
        const flip = -ez * h * nxv + ex * h * nzv < 0;
        const A = flip ? [bx, bz] : [ax, az], B = flip ? [ax, az] : [bx, bz];
        const quad = [[A[0], Y, A[1]], [B[0], Y, B[1]], [B[0], DROP, B[1]], [A[0], Y, A[1]], [B[0], DROP, B[1]], [A[0], DROP, A[1]]];
        for (const q of quad) { sp.push(q[0], q[1], q[2]); sn.push(nxv, 0, nzv); }
      }
    }
  }
  const top = new THREE.BufferGeometry();
  top.setAttribute('position', new THREE.Float32BufferAttribute(tp, 3));
  top.setAttribute('normal', new THREE.Float32BufferAttribute(new Float32Array(tp.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  top.setAttribute('color', new THREE.Float32BufferAttribute(tc, 3));
  const side = new THREE.BufferGeometry();
  side.setAttribute('position', new THREE.Float32BufferAttribute(sp, 3));
  side.setAttribute('normal', new THREE.Float32BufferAttribute(sn, 3));
  return { top, side };
}

/** 배치 씨앗(지역마다 달리 해 같은 배치 규칙도 다른 배치로 놓인다) */
const LAYOUT_SEED: Partial<Record<MapId, number>> = { r1: 1301, r3: 3301, r4: 4401, r5: 5501, r6: 6601 };
type RGB = readonly [number, number, number];
/** 지역판 화로 불꽃 색(바깥, 심). r3은 기본 따뜻한 횃불을 쓴다 */
const REGION_FLAME: Readonly<Record<KitRegion, readonly [RGB, RGB]>> = {
  r3: [[0.95, 0.22, 0.04], [1.0, 0.62, 0.22]],
  r4: [[0.25, 0.85, 0.05], [0.8, 1.0, 0.35]],
  r5: [[1.0, 0.5, 0.06], [1.0, 0.86, 0.42]],
  r6: [[0.55, 0.12, 1.0], [1.0, 0.62, 1.0]],
};
/** 지역판 결정·버섯 발광 색(r3은 기본 청록) */
const REGION_GLOW: Readonly<Record<KitRegion, RGB>> = { r3: [0.25, 0.85, 1.0], r4: [0.5, 1.0, 0.2], r5: [0.2, 0.95, 0.82], r6: [1.0, 0.25, 0.85] };
/** 지역판 장식 웅덩이 */
const REGION_POOL: Readonly<Record<KitRegion, PoolTheme>> = { r3: 'ice', r4: 'poison', r5: 'oasis', r6: 'abyss' };
/** 지역판 빛줄기 색·세기 */
const REGION_SHAFT: Readonly<Record<KitRegion, readonly [RGB, number]>> = {
  r3: [[0.55, 0.78, 1.0], 0.22],
  r4: [[0.55, 0.85, 0.4], 0.16],
  r5: [[1.0, 0.76, 0.4], 0.62],
  r6: [[0.72, 0.45, 1.0], 0.14],
};

export class EnvVisual {
  readonly root = new THREE.Group();
  readonly layout: EnvLayout;
  private readonly owned: { dispose(): void }[] = [];
  private started: Promise<EnvResult> | null = null;
  /** 지역판 키트·텍스처(c023·C2): r3 서리 묘지·r4 가라앉은 사원·r5 황금 모래 폐도·r6 심연의 성채. ?envlegacy면 null(지역1 키트 + 색 보정, 전후 비교) */
  readonly region: KitRegion | null;
  /** 대기 입자 상자 가운데(카메라가 보는 바닥 가운데, cull이 갱신) */
  private drift: { value: THREE.Vector3 } | null = null;

  constructor(private readonly map: MapDef, private readonly grid: MapGrid, private readonly quality: QualityLevel) {
    this.root.name = 'env-' + map.id;
    this.region = kitRegionOf(map.id, legacyFromSearch(typeof location === 'undefined' ? null : location.search));
    // 배치 씨앗: 지역마다 달리 해 같은 유적 키트도 다른 배치로 놓인다(R3 3301, R4 4401, D3a · R5 5501, R6 6601, C2)
    this.layout = layoutMap(map, grid, { seed: LAYOUT_SEED[map.id] ?? 707, density: DENSITY[quality] });
  }

  /** 키트·텍스처를 읽고 메시를 만든다(처음 부를 때 한 번만 시작한다). */
  start(): Promise<EnvResult> {
    this.started ??= this.build(this.quality);
    return this.started;
  }

  private add<T extends THREE.Object3D>(o: T | null): T | null {
    if (o) {
      this.root.add(o);
      const m = o as unknown as THREE.Mesh;
      if (m.geometry) this.owned.push(m.geometry);
      if (m.material) this.owned.push(m.material as THREE.Material);
    }
    return o;
  }

  private async build(quality: QualityLevel): Promise<EnvResult> {
    const L = this.layout;
    const village = this.map.id === 'village';
    const ember = this.map.id === 'r2';
    const mood = moodOf(this.map);
    const region = this.region;
    const look = region ? REGION_LOOK[region] : null;
    const rtex = region ? REGION_TEXTURES[region] : null;
    // 지역·마을 색 보정(D3a): 키트 인스턴스 색과 바닥 색에 곱한다(서리 묘지 청회·늪 사원 이끼빛). 지역판이면 거의 흰 곱(c023)
    const grade = gradeOf(this.map, region !== null);
    const kitGrade = new THREE.Color(grade ? grade.kit : 0xffffff);
    const haze = new THREE.Color(mood.haze);
    const kinds = new Set<KitKind>();
    for (const p of L.placements) {
      kinds.add(p.kind);
      if (p.alt) kinds.add(p.alt.kind);
    }
    const kitList = [...kinds];
    // 지역판 텍스처를 못 읽으면 r1 텍스처로 대신한다(키트와 같은 대체 규칙)
    const orR1 = (rel: string | undefined, r1: string, mirror = false): Promise<THREE.Texture | null> =>
      rel ? loadTexture(rel, mirror).then((t) => t ?? loadTexture(r1, mirror)) : loadTexture(r1, mirror);
    const [assets, floorTex, grassTex, stoneTex, pano] = await Promise.all([
      Promise.all(kitList.map((k) => track(loadKit(k, region)))),
      ember ? Promise.resolve(null) : track(orR1(rtex?.floor, village ? 'village/tex/plaza_cobble.webp' : 'r1/tex/floor_moss_stone.webp')),
      ember ? Promise.resolve(null) : track(orR1(rtex?.ground, 'r1/tex/ground_moss_grass.webp')),
      track(orR1(rtex?.floor, 'r1/tex/floor_moss_stone.webp')),
      ember ? Promise.resolve(null) : track(orR1(rtex?.pano, village ? 'village/tex/bg_village_panorama.webp' : 'r1/tex/bg_r1_panorama.webp', true)).then(
        (t) => t ?? (village ? loadTexture('r1/tex/bg_r1_panorama.webp', true) : null),
      ),
    ]);
    const kit = new Map<KitKind, KitAsset>();
    kitList.forEach((k, i) => { const a = assets[i]; if (a) kit.set(k, a); });

    // 원경(가장 먼저, 깊이 없이)
    // 두 파노라마 모두 지평선(바다·산 줄)이 그림 위에서 약 28% 높이(v ≈ 0.72)
    if (!ember) {
      const bd = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backdropMaterial(pano, haze, 2.5, 0.72, village ? 70 : 90));
      bd.frustumCulled = false;
      bd.renderOrder = -1000;
      this.add(bd);
    }

    // 타일 바닥
    let floorOk = false;
    if (floorTex) {
      const sh = L.shade;
      const shadeTex = new THREE.DataTexture(sh.data, sh.width, sh.height, THREE.RedFormat, THREE.UnsignedByteType);
      shadeTex.magFilter = shadeTex.minFilter = THREE.LinearFilter;
      shadeTex.needsUpdate = true;
      this.owned.push(shadeTex);
      const parts: THREE.BufferGeometry[] = [];
      for (const f of this.map.floors) {
        const g = new THREE.PlaneGeometry(f.x1 - f.x0, f.z1 - f.z0);
        g.rotateX(-Math.PI / 2);
        g.translate((f.x0 + f.x1) / 2, 0, (f.z0 + f.z1) / 2);
        parts.push(g);
      }
      const geo = parts.length === 1 ? parts[0] : mergeSimple(parts);
      // 마을은 기존 보행감(2.2m)을 유지하고, 유적은 5.25m로 키워 작은 판석 격자 인상을 줄인다.
      // 월드 저주파 얼룩은 두 장면에서 같은 floorMaterial을 거쳐 적용된다.
      const mesh = new THREE.Mesh(geo, floorMaterial(floorTex, village ? 2.2 : 5.25, { tex: shadeTex, originX: sh.originX, originZ: sh.originZ, width: sh.width, height: sh.height }, village, grassTex, grade, look));
      mesh.receiveShadow = true;
      this.add(mesh);
      floorOk = true;
      if (village) {
        const inlay = new THREE.Mesh(compassGeometry(0, 0), inlayMaterial(floorTex));
        inlay.receiveShadow = true;
        this.add(inlay);
      }
    }

    // 고원 풀밭 + 낭떠러지(바닥 셀 아래는 건너뛴다)
    if (!ember) {
      const pf = L.plateau;
      const g = this.grid;
      const off = Math.round(g.originX - pf.originX);
      const offZ = Math.round(g.originZ - pf.originZ);
      const plateau = buildPlateau(pf, (i, j) => {
        const gx = i - off, gz = j - offZ;
        if (gx < 0 || gz < 0 || gx >= g.width || gz >= g.height) return false;
        return g.cells[gz * g.width + gx] !== 0 && floorOk;
      });
      const top = new THREE.Mesh(plateau.top, groundMaterial(grassTex, village, grade, look));
      top.receiveShadow = true;
      this.add(top);
      this.add(new THREE.Mesh(plateau.side, cliffMaterial(stoneTex, haze, look)));
    }

    // 키트 인스턴싱: 종류당 1개
    const replaced = new Set<number>();
    const tops = new Map<number, number>();
    const groups = new Map<KitKind, Placement[]>();
    for (const p0 of L.placements) {
      const p = kit.has(p0.kind) ? p0 : p0.alt && kit.has(p0.alt.kind) ? p0.alt : null;
      if (!p) continue;
      if (p0.landmark >= 0) {
        replaced.add(p0.landmark);
        tops.set(p0.landmark, ember ? Math.max(tops.get(p0.landmark) ?? 0, p.y + p.h) : scaleOf(p, kit.get(p.kind)!)[1] * kit.get(p.kind)!.h);
      }
      let gr = groups.get(p.kind);
      if (!gr) groups.set(p.kind, (gr = []));
      gr.push(p);
    }
    const shadows = quality !== 'low';
    const col = new THREE.Color();
    const contacts: { x: number; y: number; z: number; rx: number; rz: number; rot: number; a: number }[] = [];
    for (const [kind, list] of groups) {
      const a = kit.get(kind)!;
      const mat = kitMaterial(a, kind, stoneTex, ember, a.region !== 'base' ? look : null);
      this.owned.push(mat);
      const n = list.length;
      const geo = a.geometry;
      const cutSrc = new Float32Array(n * 4);
      const cut = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
      cut.setUsage(THREE.DynamicDrawUsage);
      // 키트 지오메트리는 맵 사이에서 공유하므로 인스턴스 속성은 지오메트리 사본에 단다(정점 버퍼는 공유)
      const g = new THREE.BufferGeometry();
      if (geo.index) g.setIndex(geo.index);
      for (const name of ['position', 'normal', 'uv']) {
        const at = geo.getAttribute(name);
        if (at) g.setAttribute(name, at);
      }
      g.setAttribute('aCut', cut);
      const mesh = new THREE.InstancedMesh(g, mat, n);
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      const matSrc = new Float32Array(n * 16);
      const colSrc = new Float32Array(n * 3);
      const pos = new Float32Array(n * 3);
      list.forEach((p, i) => {
        const [sx, sy, sz] = scaleOf(p, a);
        const ca = CONTACT[kind];
        // 나무는 줄기 둘레만(잎 지붕 전체를 덮으면 바닥이 검은 원으로 보인다)
        // 반경 = 발자국 반폭 + 0.7m(그림자가 소품 밖으로 번져 나와야 보인다)
        const foot = kind === 'tree' || kind === 'tree_lo' ? 0.16 : 0.55;
        if (ca) contacts.push({ x: p.x, y: p.y, z: p.z, rx: a.w * sx * foot + 0.7, rz: a.d * sz * foot + 0.7, rot: p.rot, a: ca });
        compose(matSrc, i * 16, p.x, p.y, p.z, p.rot, 0, sx, sy, sz);
        cutSrc[i * 4] = p.x; cutSrc[i * 4 + 1] = p.z; cutSrc[i * 4 + 2] = p.cut; cutSrc[i * 4 + 3] = sy;
        col.setHex(p.tint);
        // 지역판 키트는 이미 지역 색으로 칠해져 있다: 배치 규칙의 이끼빛 변주(0xcfe3bd 등)는 밝기만 남긴다
        if (a.region !== 'base') col.setScalar(0.2126 * col.r + 0.7152 * col.g + 0.0722 * col.b);
        col.multiply(kitGrade);
        colSrc[i * 3] = col.r; colSrc[i * 3 + 1] = col.g; colSrc[i * 3 + 2] = col.b;
        mesh.setColorAt(i, col);
        pos[i * 3] = p.x; pos[i * 3 + 1] = p.z; pos[i * 3 + 2] = 0.5 * Math.max(a.w * sx, a.d * sz, a.h * sy);
      });
      (mesh.instanceMatrix.array as Float32Array).set(matSrc);
      (cut.array as Float32Array).set(cutSrc);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.castShadow = shadows && CASTERS.has(kind);
      mesh.receiveShadow = true;
      mesh.name = 'kit-' + kind;
      this.root.add(mesh);
      this.owned.push(g);
      this.batches.push({ mesh, cut, n, mat: matSrc, cutSrc, col: colSrc, pos });
    }
    // 효과
    this.add(contactField(contacts));
    // 지역판(c023·C2): r3은 풀·꽃 대신 서리 더미(꽃 자리는 비움), r4는 늪 갈대·포자 꽃, r5는 마른 사막 풀, r6은 흑요석 파편
    if (region === 'r3') {
      this.add(cardField(L.tufts, 'frost', false));
    } else if (region === 'r4') {
      this.add(cardField(L.tufts, 'reed', false));
      this.add(cardField(L.flowers, 'spore', false));
    } else if (region === 'r5') {
      this.add(cardField(L.tufts, 'dry', false));
    } else if (region === 'r6') {
      this.add(cardField(L.tufts, 'shard', false));
    } else {
      this.add(cardField(L.tufts, 'tuft', village));
      this.add(cardField(L.flowers, 'flower', village));
    }
    // r4 화로는 연두 불꽃(독 사원), r3은 따뜻한 횃불을 그대로 둬 차가운 청록과 대비(콘셉트 r3-dungeon), r5는 금빛, r6은 보랏빛 영혼불
    const fl = REGION_FLAME[region ?? 'r3'];
    if (kit.has('brazier')) this.add(region && region !== 'r3' ? flameField(L.flames, fl[0], fl[1]) : flameField(L.flames));
    // 결정(r3)은 청록, 포자 버섯(r4)은 연두, 결정·항아리(r5)는 청록, 공허 결정(r6)은 자홍 발광
    if (kit.has('mushrooms')) this.add(region && region !== 'r3' ? glowField(L.glows, REGION_GLOW[region]) : glowField(L.glows));
    // 등불 빛망울: 2.6m는 흰 얼룩처럼 주변을 덮었다(dream-loop 마을 4회차 P1) → 1.1m, 진한 주황
    if (kit.has('v_lamppost')) this.add(glowField(L.lamps.map((e) => ({ ...e, s: 1.1 })), [0.9, 0.42, 0.12]));
    // 담쟁이는 r3·r5·r6에서 끈다(서리 묘지·사막·허공 성채에 초록 덩굴 없음). r4는 잠긴 사원 덩굴로 둔다
    if (region !== 'r3' && region !== 'r5' && region !== 'r6') this.add(ivyField(L.ivy));
    this.add(poolField(L.pools, region ? REGION_POOL[region] : 'water'));
    // 빛줄기: r3은 차갑고 약한 달빛, r4는 녹색 안개 기둥, r5는 낮은 금빛 해 기둥(강하게), r6은 보라 기운(약하게)
    const sunDir = new THREE.Vector3(mood.sunX, mood.sunY, mood.sunZ);
    const sh = region ? REGION_SHAFT[region] : null;
    this.add(sh ? shaftField(L.shafts, sunDir, sh[0], sh[1]) : shaftField(L.shafts, sunDir));
    // 대기 입자(드로우콜 +1): r3 눈송이, r4 포자, r5 모래 먼지, r6 보라 불씨
    if (region) {
      const d = driftField(REGION_DRIFT[region], DENSITY[quality]);
      if (d) {
        this.add(d.mesh);
        this.drift = d.center;
      }
    }
    if (village) {
      const npcs = new VillageNpcs();
      await npcs.load();
      this.root.add(npcs.root);
      this.npcs = npcs;
    }

    return { replaced, tops, walls: kit.has('wall'), floor: floorOk };
  }

  private npcs: VillageNpcs | null = null;

  /** 매 프레임 장식 배우(마을 주민) 애니메이션 */
  animate(nowMs: number): void {
    this.npcs?.update(nowMs);
  }

  private readonly batches: KitBatch[] = [];
  private readonly lastCull = new Float32Array([1e9, 1e9, 0]);

  /**
   * 카메라 시야가 바닥(y 0~7m)에 닿는 사각형 + 여유 안의 인스턴스만 앞쪽으로 모아 그린다.
   * 카메라 목표·거리가 거의 그대로면 아무것도 하지 않는다. camera가 null이면 모두 그린다(미리 컴파일용).
   */
  cull(camera: THREE.PerspectiveCamera | null): void {
    const b = this.batches;
    if (b.length === 0) return;
    if (!camera) {
      for (const k of b) this.fill(k, -1e9, 1e9, -1e9, 1e9);
      this.lastCull[0] = 1e9;
      return;
    }
    const cp = camera.position;
    const lc = this.lastCull;
    if (Math.abs(cp.x - lc[0]) < 1.5 && Math.abs(cp.z - lc[1]) < 1.5 && Math.abs(cp.y - lc[2]) < 1.5) return;
    lc[0] = cp.x; lc[1] = cp.z; lc[2] = cp.y;
    let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
    camera.updateMatrixWorld();
    for (let c = 0; c < 4; c++) {
      _ndc.set(c & 1 ? 1 : -1, c & 2 ? 1 : -1, 0.5).unproject(camera);
      _dir.copy(_ndc).sub(cp).normalize();
      for (let hgt = 0; hgt <= 7; hgt += 7) {
        // 이 높이 평면과 만나는 거리(하늘을 보면 120m에서 자른다)
        const t = _dir.y < -1e-3 ? Math.min(120, (cp.y - hgt) / -_dir.y) : 120;
        const x = cp.x + _dir.x * t, z = cp.z + _dir.z * t;
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (z < z0) z0 = z;
        if (z > z1) z1 = z;
      }
    }
    for (const k of b) this.fill(k, x0 - CULL_MARGIN, x1 + CULL_MARGIN, z0 - CULL_MARGIN, z1 + CULL_MARGIN);
    // 대기 입자 상자를 화면 가운데 시선이 바닥에 닿는 점으로(할당 없음)
    if (this.drift) {
      _ndc.set(0, 0, 0.5).unproject(camera);
      _dir.copy(_ndc).sub(cp).normalize();
      const t = _dir.y < -1e-3 ? Math.min(60, cp.y / -_dir.y) : 30;
      this.drift.value.set(cp.x + _dir.x * t, 0, cp.z + _dir.z * t);
    }
  }

  private fill(k: KitBatch, x0: number, x1: number, z0: number, z1: number): void {
    const dm = k.mesh.instanceMatrix.array as Float32Array;
    const dc = k.cut.array as Float32Array;
    const dcol = k.mesh.instanceColor!.array as Float32Array;
    const { pos, mat, cutSrc, col } = k;
    let m = 0;
    for (let i = 0; i < k.n; i++) {
      const x = pos[i * 3], z = pos[i * 3 + 1], r = pos[i * 3 + 2];
      if (x + r < x0 || x - r > x1 || z + r < z0 || z - r > z1) continue;
      for (let j = 0; j < 16; j++) dm[m * 16 + j] = mat[i * 16 + j];
      for (let j = 0; j < 4; j++) dc[m * 4 + j] = cutSrc[i * 4 + j];
      for (let j = 0; j < 3; j++) dcol[m * 3 + j] = col[i * 3 + j];
      m++;
    }
    k.mesh.count = m;
    k.mesh.instanceMatrix.needsUpdate = true;
    k.cut.needsUpdate = true;
    k.mesh.instanceColor!.needsUpdate = true;
  }

  dispose(): void {
    this.npcs?.dispose();
    for (const o of this.owned) o.dispose();
    this.owned.length = 0;
    this.root.clear();
  }
}

function mergeSimple(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [], uvs: number[] = [], idx: number[] = [];
  let base = 0;
  for (const p of parts) {
    const pp = p.getAttribute('position'), nn = p.getAttribute('normal'), uu = p.getAttribute('uv'), ii = p.getIndex()!;
    for (let i = 0; i < pp.count; i++) {
      pos.push(pp.getX(i), pp.getY(i), pp.getZ(i));
      nor.push(nn.getX(i), nn.getY(i), nn.getZ(i));
      uvs.push(uu.getX(i), uu.getY(i));
    }
    for (let i = 0; i < ii.count; i++) idx.push(ii.getX(i) + base);
    base += pp.count;
    p.dispose();
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  return g;
}

/**
 * 나침반 상감 지오메트리(바닥 y=0.012): 바깥 띠(r 4.7~5.2), 안쪽 띠(r 3.15~3.35), 8방 별(정방 4개는 길게, 대각 4개는 짧게).
 * 별 꼭짓점 하나는 좌우 반쪽을 밝은 사암/청회 슬레이트로 나눠 입체 나침반처럼 보인다. 삼각형 약 150개.
 */
function compassGeometry(cx: number, cz: number): THREE.BufferGeometry {
  const pos: number[] = [], col: number[] = [];
  const Y = 0.012;
  const LIGHT = [0.98, 0.84, 0.6], SLATE = [0.34, 0.38, 0.48], DARK = [0.42, 0.34, 0.26];
  const tri = (ax: number, az: number, bx: number, bz: number, qx: number, qz: number, c: number[]): void => {
    // 위를 향하도록 감는다
    const cross = (bx - ax) * (qz - az) - (bz - az) * (qx - ax);
    const pts = cross < 0 ? [ax, az, bx, bz, qx, qz] : [ax, az, qx, qz, bx, bz];
    for (let k = 0; k < 3; k++) { pos.push(cx + pts[k * 2], Y, cz + pts[k * 2 + 1]); col.push(c[0], c[1], c[2]); }
  };
  const ring = (r0: number, r1: number, seg: number, c: number[]): void => {
    for (let i = 0; i < seg; i++) {
      const a0 = (i / seg) * Math.PI * 2, a1 = ((i + 1) / seg) * Math.PI * 2;
      const p = [Math.cos(a0), Math.sin(a0), Math.cos(a1), Math.sin(a1)];
      tri(p[0] * r0, p[1] * r0, p[0] * r1, p[1] * r1, p[2] * r1, p[3] * r1, c);
      tri(p[0] * r0, p[1] * r0, p[2] * r1, p[3] * r1, p[2] * r0, p[3] * r0, c);
    }
  };
  ring(5.6, 6.2, 56, DARK);
  ring(4.45, 4.7, 44, SLATE);
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    const main = k % 2 === 0;
    const tip = main ? 6.15 : 5.5, base = 4.7, half = main ? 0.16 : 0.12;
    const tx = Math.cos(a) * tip, tz = Math.sin(a) * tip;
    const lx = Math.cos(a - half) * base, lz = Math.sin(a - half) * base;
    const rx = Math.cos(a + half) * base, rz = Math.sin(a + half) * base;
    const mx = Math.cos(a) * base, mz = Math.sin(a) * base;
    tri(mx, mz, lx, lz, tx, tz, main ? LIGHT : SLATE);
    tri(mx, mz, rx, rz, tx, tz, main ? SLATE : LIGHT);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(new Float32Array(pos.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  return g;
}

export { MAP_MOOD } from './mood';
export { envProgress } from './kits';
